import { useEffect, useState } from 'react';
import { TravelExplore } from '@mui/icons-material';
import { Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';

import DateRangeSelector from '~/components/Form/DateRangeSelector';
import LocationSelector from '~/components/Form/LocationSelector';
import TravelersOptionsSelector from '~/components/Form/TravelersOptionsSelector';

import { FlightSearchFormValidationMessages, FlightSearchParams, Place } from '~/types';
import { Currency, FlightType, TripTravellers } from '~/types/common';
import { useSelector } from 'react-redux';

interface TripDotComSearchProps {
  hideSearchButton?: boolean;
}

interface TripComParams {
  dcity: string; // departure city iata codes
  acity: string; // arrival city iata
  ddate: string; // departure date (YYYY-MM-DD)
  rdate?: string; // return date (YYYY-MM-DD)
  flighttype: 'ow' | 'rt'; // ow: one-way, rt: round trip
  quantity: number; // total travelers
  childqty: number;
  babyqty: number; // infants
  class: 'y' | 's' | 'f' | 'c'; // cabin class: y=economy, s=premium economy, c=business, f=first
  allianceId: string;
  sid: string;
  trip_sub1: string;
  trip_sub3?: string;
}

function TripDotComSearch({ hideSearchButton }: TripDotComSearchProps) {
  const theme = useTheme();
  const currency = useSelector<any, Currency>(({ app }) => app.currency);

  const [searchParams, setSearchParams] = useState<FlightSearchParams>({
    from: null,
    to: null,
    type: 'return',
    travelClass: 'ECONOMY',
    passengers: {
      adults: 1,
      children: 0,
      infants: 0,
    },
    departureDate: null,
    returnDate: null,
    currencyCode: currency,
  });

  const [validationMode, setValidationMode] = useState(false);
  const [validationMessages, setValidationMessages] = useState<FlightSearchFormValidationMessages>({
    to: [],
    from: [],
    departure: [],
    return: [],
  });

  useEffect(() => {
    setSearchParams(previousSearchParams => ({
      ...previousSearchParams,
      currencyCode: currency,
    }));
  }, [currency]);

  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const handleFlightTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = {
      ...searchParams,
      type: event.target.value as FlightType,
    };
    setSearchParams(newSearchParams);
  };

  const handleTravelClassChange = (value: string) => {
    const newSearchParams = {
      ...searchParams,
      travelClass: value,
    };
    setSearchParams(newSearchParams);
  };

  const handleAdultPassangersChange = (
    _:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => {
    const newSearchParams = {
      ...searchParams,
      passengers: {
        ...searchParams.passengers,
        adults: value || 1,
      } as TripTravellers,
    };
    setSearchParams(newSearchParams);
  };

  const handleChildrenPassangersChange = (
    _:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => {
    setSearchParams(previousSearchParams => ({
      ...previousSearchParams,
      passengers: {
        ...previousSearchParams.passengers,
        children: value || 0,
      } as TripTravellers,
    }));
  };

  const handleInfantPassangersChange = (
    _:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => {
    const newSearchParams = {
      ...searchParams,
      passengers: {
        ...searchParams.passengers,
        infants: value || 0,
      } as TripTravellers,
    };
    setSearchParams(newSearchParams);
  };

  const handleFromLocationChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: Place | null | string,
  ) => {
    const newSearchParams = {
      ...searchParams,
      from: value,
    } as FlightSearchParams;
    setSearchParams(newSearchParams);
    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const handleToLocationChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: Place | null | string,
  ) => {
    const newSearchParams = {
      ...searchParams,
      to: value,
    } as FlightSearchParams;
    setSearchParams(newSearchParams);
    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const handleDepartureDateChange = (date: Date | null) => {
    let newSearchParams = {
      ...searchParams,
      departureDate: date,
    } as FlightSearchParams;

    if (date !== null) {
      setSearchParams(newSearchParams);
    }

    if (date && searchParams.returnDate && date > searchParams.returnDate) {
      newSearchParams = {
        ...searchParams,
        returnDate: date,
      };
      setSearchParams(newSearchParams);
    }

    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const handleReturnDateChange = (date: Date | null) => {
    const newSearchParams = {
      ...searchParams,
      returnDate: date,
    } as FlightSearchParams;

    if (date !== null) {
      setSearchParams(newSearchParams);
    }

    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const cabinClassMap: Record<string, 'y' | 's' | 'f' | 'c'> = {
    ECONOMY: 'y',
    PREMIUM_ECONOMY: 's',
    BUSINESS: 'c',
    FIRST: 'f',
  };

  const buildTripComUrl = (): string => {
    const flightType = searchParams.type === 'oneway' ? 'ow' : 'rt';
    const departureCity = (searchParams.from as Place)?.iata || '';
    const arrivalCity = (searchParams.to as Place)?.iata || '';

    const formatDate = (date: Date | null | undefined): string => {
      if (!date) return '';
      return date.toISOString().split('T')[0]; // YYYY-MM-DD
    };

    const departureDate = formatDate(searchParams.departureDate);
    const returnDate = formatDate(searchParams.returnDate);

    const params: TripComParams = {
      dcity: departureCity,
      acity: arrivalCity,
      ddate: departureDate,
      rdate: returnDate,
      flighttype: flightType,
      quantity: searchParams.passengers.adults,
      childqty: searchParams.passengers.children,
      babyqty: searchParams.passengers.infants,
      class: cabinClassMap[searchParams.travelClass] || 'y',
      allianceId: '8242207',
      sid: '312434032',
      trip_sub1: '',
      trip_sub3: 'S17082746',
    };

    // Build URL path based on flight type
    let urlPath = '';
    if (flightType === 'ow') {
      urlPath = `/${departureCity}-to-${arrivalCity}`;
    } else {
      urlPath = `/${departureCity}-to-${arrivalCity}`;
    }

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.set('flighttype', params.flighttype);
    queryParams.set('dcity', params.dcity);
    queryParams.set('acity', params.acity);
    queryParams.set('ddate', params.ddate);
    if (params.rdate) {
      queryParams.set('rdate', params.rdate);
    }
    queryParams.set('quantity', params.quantity.toString());
    queryParams.set('childqty', params.childqty.toString());
    queryParams.set('babyqty', params.babyqty.toString());
    queryParams.set('class', params.class);
    queryParams.set('allianceId', params.allianceId);
    queryParams.set('sid', params.sid);
    queryParams.set('trip_sub1', params.trip_sub1);
    if (params.trip_sub3) {
      queryParams.set('trip_sub3', params.trip_sub3);
    }
    // Add currency parameter supported by Trip.com (e.g. &curr=EUR)
    const currCode = currency || 'USD';
    queryParams.set('curr', currCode);

    return `https://www.trip.com/flights${urlPath}/tickets-${params.dcity}-${params.acity}?${queryParams.toString()}`;
  };

  const handleSearchSubmitted = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const messages = getSearchValidationMessages(searchParams);
    setValidationMessages(messages);

    if (
      messages.to.length ||
      messages.from.length ||
      messages.departure.length ||
      (searchParams.type === 'return' && messages.return.length)
    ) {
      setValidationMode(true);
    } else {
      setValidationMode(false);
      const tripComUrl = buildTripComUrl();
      window.open(tripComUrl, '_blank');
    }
  };

  return (
    <Box component="form" noValidate onSubmit={handleSearchSubmitted}>
      <Grid container spacing={1}>
        <Grid item xs={12} className="top-options">
          <TravelersOptionsSelector
            onAdultPassangersChange={handleAdultPassangersChange}
            onChildrenPassangersChange={handleChildrenPassangersChange}
            onFlightTypeChange={handleFlightTypeChange}
            onInfantPassangersChange={handleInfantPassangersChange}
            onTravelClassChange={handleTravelClassChange}
            tripOptions={{
              type: searchParams.type,
              passengers: searchParams.passengers,
              travelClass: searchParams.travelClass,
            }}
          />
        </Grid>

        {/* Location Selectors */}
        <Grid item xs={isMedium ? 12 : (searchParams.type === 'oneway' ? 6 : 5)}>
          <Grid container spacing={2}>
            <Grid item xs={isSmall ? 12 : 6}>
              <LocationSelector
                errorHelperText={validationMode ? validationMessages.from : undefined}
                label="From"
                location={searchParams.from}
                name="from"
                onLocationChange={handleFromLocationChange}
                placeholder="From"
                required
              />
            </Grid>
            <Grid item xs={isSmall ? 12 : 6}>
              <LocationSelector
                errorHelperText={validationMode ? validationMessages.to : undefined}
                label="To"
                location={searchParams.to}
                name="to"
                onLocationChange={handleToLocationChange}
                placeholder="To"
                required
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Date Range Selector */}
        <Grid item xs={isMedium ? 12 : (searchParams.type === 'oneway' ? 4 : 5)} className="trip-date-range-selector">
          <DateRangeSelector
            dateRange={{
              from: searchParams.departureDate,
              to: searchParams.returnDate,
            }}
            fromDateRequired
            fromHelperText={validationMode ? validationMessages.departure : undefined}
            fromLabel={searchParams.type === 'oneway' ? '(One Way) Departure *' : 'Departure *'}
            onFromDateChange={handleDepartureDateChange}
            onToDateChange={handleReturnDateChange}
            singleDateMode={searchParams.type === 'oneway'}
            toDateRequired={searchParams.type === 'return'}
            toHelperText={validationMode ? validationMessages.return : undefined}
            toLabel="Return *"
          />
        </Grid>

        {/* Search Button */}
        <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          {hideSearchButton ? null : (
            <Button
              fullWidth
              startIcon={<TravelExplore />}
              sx={{ maxHeight: 56 }}
              type="submit"
              variant="contained"
            >
              Search Flights
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default TripDotComSearch;

const getSearchValidationMessages = (
  fields: FlightSearchParams,
): FlightSearchFormValidationMessages => {
  const validationMessages: FlightSearchFormValidationMessages = {
    to: [],
    from: [],
    departure: [],
    return: [],
  };

  if (!fields.from) {
    validationMessages.from.push('From is required');
  }

  if (!fields.to) {
    validationMessages.to.push('To is required');
  }

  if (!fields.departureDate) {
    validationMessages.departure.push('Departure date is required');
  }

  if (fields.type === 'return' && !fields.returnDate) {
    validationMessages.return.push('Return date is required');
  }

  if (fields.departureDate && fields.returnDate && fields.departureDate > fields.returnDate) {
    validationMessages.return.push('Return date must be after departure date');
  }

  return validationMessages;
};
